import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SearchEngineFieldTextRomesInput } from '@domains/search-engine/entities/text-romes-input-search-field';
import { RomesLabels } from '@models/apprentice/romelabels';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApprenticeWebService } from '@webservices/job-api-v2/apprentice.webservice';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, shareReplay, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-text-romes-input-search',
  templateUrl: './text-romes-input-search.component.html',
  styleUrls: ['./text-romes-input-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextRomesInputSearchComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextRomesInputSearchComponent implements OnInit, ControlValueAccessor {
  @Input() field!: SearchEngineFieldTextRomesInput;
  @Input() liveSearch!: boolean;
  @Output() hasChanged = new EventEmitter<void>();

  formInput: FormGroup;
  romes$!: Observable<RomesLabels[]>;

  private _value?: string;

  get value(): string {
    return this._value ?? '';
  }

  set value(val: string) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }
  get metier(): FormControl {
    return this.formInput.get('metier') as FormControl;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly apprenticeWebService: ApprenticeWebService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.formInput = this.fb.group({
      metier: undefined,
    });
  }

  ngOnInit(): void {
    this.romes$ = this.metier.valueChanges.pipe(
      debounceTime(300),
      filter((q) => !!q && q.length > 0),
      distinctUntilChanged(),
      switchMap((q) => this.apprenticeWebService.getRomeLabels(q)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.metier.valueChanges.pipe(untilDestroyed(this)).subscribe((metier) => {
      this.value = metier;
      if (this.liveSearch) {
        this.hasChanged.emit();
      }
    });
  }

  onChange: (val: string) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val: string): void {
    this.value = val;
    this.metier.setValue(val);
  }
  registerOnChange(fn: (val: string) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  clearValue(): void {
    this.metier.setValue(undefined);
  }

  trackByRomeLabel(_: number, rome: RomesLabels): string {
    return rome.label;
  }
}

@NgModule({
  imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule, MatInputModule, MatOptionModule, MatIconModule],
  declarations: [TextRomesInputSearchComponent],
  exports: [TextRomesInputSearchComponent],
})
export class SearchEngineTextRomesInputSearchModule {}
